import type React from 'react';
import { useMemo } from 'react';
import { type Product, type SaleStatDays, saleStat, saleStatDays } from '../../data/product';
import { formatCasesQuantity, formatUnitsQuantity } from '../../helpers/format-quantity';
import { HoverCard } from '../popover/hover-card';

export function SalesDataPopover({
  children,
  product,
  isCasesView = false,
  salesStat,
}: {
  children: React.ReactElement;
  product: Product;
  isCasesView?: boolean;
  salesStat: SaleStatDays;
}) {
  return (
    <HoverCard
      content={
        <SalesDataPopoverContent
          product={product}
          isCasesView={isCasesView}
          salesStat={salesStat}
        />
      }
    >
      {children}
    </HoverCard>
  );
}

function SalesDataPopoverContent({
  product,
  isCasesView = false,
  salesStat,
}: {
  product: Product;
  isCasesView?: boolean;
  salesStat: SaleStatDays;
}) {
  const salesData = useMemo(() => {
    const pastData = saleStatDays
      .filter((period) => period >= 30)
      .map((period) => ({
        period,
        pastValue: isCasesView
          ? formatCasesQuantity(saleStat(product, period), product.case_qty)
          : formatUnitsQuantity(saleStat(product, period)),
      }));

    const forwardLookingHistoricalData = [
      { period: 30, value: product.stats.last_year_next_30 },
      { period: 60, value: product.stats.last_year_next_60 },
      { period: 90, value: product.stats.last_year_next_90 },
      { period: 120, value: product.stats.last_year_next_120 },
      { period: 150, value: product.stats.last_year_next_150 },
      { period: 180, value: product.stats.last_year_next_180 },
      { period: 365, value: product.stats.last_365 },
    ].map((item) => ({
      period: item.period,
      value: isCasesView
        ? formatCasesQuantity(item.value, product.case_qty)
        : formatUnitsQuantity(item.value),
    }));

    // Use future data periods as the base to ensure all periods are included
    return forwardLookingHistoricalData.map((forward) => {
      const past = pastData.find((p) => p.period === forward.period);

      return {
        period: forward.period,
        pastValue: past?.pastValue || '-',
        forwardValue: forward.value,
      };
    });
  }, [product, isCasesView]);

  return (
    <div className="w-72">
      <div className="font-medium text-popover-foreground mb-2 border-b border-popover-border pb-1">
        Sales History
      </div>
      <div className="flex flex-col divide-y divide-popover-border">
        <div className="grid grid-cols-3 gap-x-2 pb-1">
          <div className="text-popover-foreground/60 font-medium min-w-0">Period (days)</div>
          <div className="text-popover-foreground/60 font-medium text-right min-w-0">
            Past Sales
          </div>
          <div className="text-popover-foreground/60 font-medium text-right min-w-0">
            Projected Sales
          </div>
        </div>

        {salesData.map(({ period, pastValue, forwardValue }) => (
          <div
            key={period}
            className={`grid grid-cols-3 gap-x-2 p-1 ${period === salesStat ? 'font-semibold' : ''}`}
          >
            <div className="tabular-nums min-w-0">{period}</div>
            <div className="text-right tabular-nums min-w-0">{pastValue}</div>
            <div className="text-right tabular-nums min-w-0">{forwardValue}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
